
// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { ActionTree, MutationTree } from 'vuex';
import { defaultGetOptions, defaultPutOptions, fetchGetWithRetryAsync, isAbortError, logErrorToConsole } from '@/utils/apiUtils';
import { IState } from './index';

const namespaced = (val: string) => `user/${val}`;

export const actionTypes = Object.freeze({
    CLAIM_ACCOUNT: namespaced('claimAccount'),
    IS_ACCOUNT_CLAIMED: namespaced('isAccountClaimed'),
    VERIFY_EMAIL: namespaced('verifyEmail'),
});

interface User {
    firstName: string,
    email: string,
}

export class State {
    user: User | null;

    constructor() {
        this.user = null;
    }
}

export const mutations: MutationTree<State> = {
    updateUser(state, user) {
        state.user = user;
    },
};

export const actions: ActionTree<State, IState> = {
    async claimAccount(_context, params) {
        const options = defaultPutOptions(
            {
                userId: params.userId,
                password: params.password,
                token: params.token,
            },
            params.signal
        );
        options.credentials = 'same-origin';

        try {
            const apiResponse = await fetch('/api/user/claim', options);

            if (apiResponse.ok) {
                return {
                    successResponse: true,
                    destination: params.destination,
                };
            }
        } catch (error) {
            if (isAbortError(error)) {
                throw error;
            }
            logErrorToConsole(error);
            return { successResponse: false };
        }
    },
    async isAccountClaimed(context, params) {
        const options = defaultGetOptions(params.signal);
        options.credentials = 'same-origin';

        const response = await fetchGetWithRetryAsync(
            `/api/account/claim/status?userId=${params.userId
            }&token=${encodeURIComponent(params.token)}&returnUrl=${params.url
            }`,
            options
        );
        const body = await response.json();

        context.commit('updateUser', body);
        return { status: response.status, body };
    },
    verifyEmail(_context, params) {
        return fetch(
            '/api/v2/customer/email/confirm',
            defaultPutOptions(
                {
                    userId: params.userId,
                    token: params.token,
                    returnUrl: params.returnUrl,
                },
                params.signal
            )
        );
    },
};


export default {
    namespaced: true,
    state: new State(),
    actions,
    mutations,
};
